import { useField, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { capitalize } from '../../utils/utils';
import DividerWithText from '../DividerWithText/DividerWithText';
import ClearEditIcon from '../Icons/ClearEditIcon';
import FileIcon from '../Icons/FileIcon';
import GreyDeleteIcon from '../Icons/GreyDeleteIcon';
import { FieldErrorInfo, InputWrapper } from './FormField';

export interface SingleDocumentUploadFieldProps {
  className?: string;
  label: string;
  placeholder?: string;
  id?: string;
  name: string;
  accept?: string;
}

const SingleDocumentUploadField: React.FC<SingleDocumentUploadFieldProps> = ({
  className,
  label,
  placeholder,
  accept,
  ...props
}) => {
  const [field, meta, helpers] = useField(props);
  const [fileList, setFileList] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const { handleBlur } = useFormikContext<any>();

  useEffect(() => {
    if (field.value) {
      const files = Array.from(field.value);

      const urls = files.map((file) =>
        URL.createObjectURL(file as any as File),
      );
      setFileList(urls);

      return () => {
        urls.forEach((url) => URL.revokeObjectURL(url));
      };
    } else {
      setFileList([]);
    }
  }, [field.value]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files && event.target.files;

    if (files) {
      const fileArray = Array.from(files);
      helpers.setValue(fileArray);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files) {
      const fileArray = Array.from(files);
      helpers.setValue(fileArray);
    }
  };

  return (
    <InputWrapper className={className}>
      <LabelText>{label}</LabelText>
      <DocumentInputField
        multiple
        name={props.name}
        id={props.name}
        type='file'
        accept={`${accept || '.pdf,application/pdf'}`}
        onChange={handleFileChange}
        onBlur={handleBlur}
      />

      {fileList?.length < 1 ? (
        <Label htmlFor={props.name || props.id}>
          <UploadBox
            isDragging={isDragging}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Placeholder>{placeholder}</Placeholder>
            <LineArea>
              <DividerWithText>OR</DividerWithText>
            </LineArea>
            <BrowseFiles>Browse files (Must be less than 15MB)</BrowseFiles>
          </UploadBox>
        </Label>
      ) : (
        <ImageBox>
          {fileList.map((fileUrl: string, index) => (
            <FilePreviewer
              key={index}
              formikField={field}
              formikHelpers={helpers}
              fileList={fileList}
              setFileList={setFileList}
            />
          ))}
        </ImageBox>
      )}
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{capitalize(meta?.error)}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default SingleDocumentUploadField;

interface FilePreviewerProps {
  formikField: any;
  formikHelpers: any;
  fileList: any[];
  setFileList: (...args: any) => void;
}

const FilePreviewer: React.FC<FilePreviewerProps> = ({
  formikField,
  formikHelpers,
  fileList,
  setFileList,
}) => {
  const handleOnDelete = (indexToDelete: number) => {
    formikHelpers.setValue(
      formikField.value.filter(
        (_: any, index: number) => index !== indexToDelete,
      ),
    );
    setFileList(
      fileList.filter((_: any, index: number) => index !== indexToDelete),
    );
  };

  return (
    <AttachedFileArea>
      <AttachedFileLabel>1 file uploaded</AttachedFileLabel>
      <AttachedFileBodyArea>
        <AttachedFileIcon>
          <FileIcon />
          <FileIconDescription>
            {formikField.value[0]?.name || ''}
          </FileIconDescription>
        </AttachedFileIcon>
        <ControlsIconArea>
          <EditFile formikHelpers={formikHelpers} />
          <div onClick={() => handleOnDelete(0)}>
            <GreyDeleteIcon />
          </div>
        </ControlsIconArea>
      </AttachedFileBodyArea>
    </AttachedFileArea>
  );
};

interface AddAnotherFileProps {
  formikHelpers: any;
}

const EditFile: React.FC<AddAnotherFileProps> = ({ formikHelpers }) => {
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files && event.target.files;

    if (files) {
      const fileArray = Array.from(files);
      formikHelpers.setValue([...fileArray]);
    }
  };

  return (
    <EditFileContainer htmlFor='editFile'>
      <DocumentInputField
        multiple
        name='editFile'
        id='editFile'
        type='file'
        accept='.pdf,application/pdf'
        onChange={handleFileChange}
      />
      <ClearEditIcon />
    </EditFileContainer>
  );
};

const AttachedFileArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
`;

const AttachedFileLabel = styled.div`
  font-weight: 600;
  font-size: 14px;
  width: 100%;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const AttachedFileBodyArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const AttachedFileIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FileIconDescription = styled.div`
  /* border: 1px solid blue; */
  font-weight: 500;
  font-size: 14px;
  max-width: 300px;
  overflow-x: hidden;
  text-overflow: clip;
  text-wrap: nowrap;
  text-overflow: ellipsis;
`;

const ControlsIconArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  & > div {
    cursor: pointer;
  }
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DocumentInputField = styled.input`
  display: none;
`;

const LabelText = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: black;
  user-select: none;
`;

const UploadBox = styled.div<{ isDragging: boolean }>`
  display: flex;
  gap: 8px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${({ theme }) => theme.palette.borderColor};
  border-radius: 6px;
  padding: 24px;
  cursor: pointer;
  background-color: ${({ isDragging }) =>
    isDragging ? 'rgba(0, 128, 255, 0.1)' : 'transparent'};
`;

const Placeholder = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #0b0b0b;
`;

const BrowseFiles = styled.div`
  color: ${({ theme }) => theme.palette.blueColor};
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
`;

const LineArea = styled.div`
  display: flex;
  align-items: center;
`;

const ImageBox = styled.div`
  display: grid;
  grid-auto-flow: column;
  width: 100%;
`;

const EditFileContainer = styled.label`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;
